import React, { useState, useEffect } from 'react';
import { Settings, Facebook, MessageCircle, Save, Loader, Sparkles, CreditCard, Check, Bell, Smartphone, Calendar, ExternalLink, RefreshCw, Link2, Unlink, AlertCircle, CheckCircle2, Trash2, ChevronDown, Globe, ClipboardList, AlertTriangle, Hand, Flag, ListChecks, CheckSquare } from 'lucide-react';
import {
    initGoogleCalendarApi,
    isSignedIn,
    requestAuthorization,
    signOut as googleSignOut,
    loadGoogleScripts,
    listCalendars,
    setSelectedCalendar,
    getSelectedCalendar
} from '../../utils/googleCalendar';
import { doc, setDoc, getDoc, collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, appId } from '../../lib/firebase';
import { useLanguage } from '../../contexts/LanguageContext';
import { settingsService, facebookService, sourcesService, fetchWithAuth, API_BASE_URL } from '../../services/api';
import { Skeleton, Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem } from '../ui';
import { formatDate } from '../../utils/helpers';

// Hard-coded integration source names (must match functions/index.js)
const INTEGRATION_SOURCES = {
    meta: 'Meta Lead Gen',
    whatsapp: 'WhatsApp Inbound'
};

export default function IntegrationsView() {
    const { t } = useLanguage();
    const [config, setConfig] = useState({
        // Facebook/Meta Lead Gen
        fbAccessToken: '',
        fbPageId: '',
        // Meta Ads reporting (dashboard spend/lead sync — System User token, ads_read)
        metaAdAccountId: '',
        metaAdsToken: '',
        // Instagram-login account token (resolves DM sender names; ~60-day life)
        igAccessToken: '',
        // WhatsApp Business API
        waProvider: 'whatsapp_cloud', // whatsapp_cloud, twilio, wati, aisensy
        waApiKey: '',
        waApiSecret: '',
        waApiEndpoint: '',
        waBusinessNumber: '',
        // WhatsApp Cloud API (Meta official)
        waPhoneNumberId: '',
        waCloudAccessToken: '',
        waBusinessAccountId: '',
        waDefaultMessage: 'Assalam o Alaikum! Area 51 Banquet Hall mein inquiry ka shukriya.',
        // Auto WhatsApp Greeting
        autoGreetingEnabled: false,
        autoGreetingMessage: `Assalam o Alaikum {{name}}! 👋

Area 51 Banquet Hall mein inquiry ka shukriya! Hamari team jaldi aap se rabta karegi.

Agar koi sawal ho toh is message ka jawab dein.

📋 Is number pe baat cheet service quality ke liye record ki jati hai.

Shukriya,
Area 51 Team`,
        // Gemini AI
        geminiApiKey: '',
        // Invoicing
        invoicingApiKey: '',
        invoicingApiEndpoint: '',
        // SMS Notifications (Twilio)
        smsEnabled: false,
        smsTwilioSid: '',
        smsTwilioToken: '',
        smsTwilioNumber: '',
        smsNotifyOnAssignment: true,
        smsNotifyOnEscalation: true,
        smsNotifyOnSiteVisit: true,
        smsNotifyOnQuoteFollowUp: true,
        // Browser Notifications
        browserNotificationsEnabled: true,
        // Google Calendar
        googleCalendarEnabled: false,
        googleCalendarClientId: '',
        googleCalendarApiKey: '',
        googleCalendarAutoSync: false,
        googleCalendarSyncSiteVisits: true,
        googleCalendarSyncCalls: true,
        googleCalendarSyncEvents: true,
        googleCalendarSyncBookings: true,
        googleCalendarSelectedId: 'primary'
    });
    const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false);
    const [connectingGoogle, setConnectingGoogle] = useState(false);
    const [availableCalendars, setAvailableCalendars] = useState([]);
    const [loadingCalendars, setLoadingCalendars] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [saveMessage, setSaveMessage] = useState('');
    const [saveError, setSaveError] = useState('');
    const [activeTab, setActiveTab] = useState('meta');
    const [initialLoading, setInitialLoading] = useState(true);
    const [leadSources, setLeadSources] = useState([]);

    // Facebook OAuth State
    const [fbConnected, setFbConnected] = useState(false);
    const [fbPages, setFbPages] = useState([]);
    const [fbLoading, setFbLoading] = useState(true);
    const [fbConnecting, setFbConnecting] = useState(false);
    const [fbDisconnecting, setFbDisconnecting] = useState(null); // pageId being disconnected

    // Auto-hide success/error message
    useEffect(() => {
        if (saveSuccess || saveError) {
            const timer = setTimeout(() => {
                setSaveSuccess(false);
                setSaveError('');
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [saveSuccess, saveError]);

    useEffect(() => {
        const loadConfig = async () => {
            setInitialLoading(true);
            try {
                const data = await settingsService.get('integrations');
                if (data) {
                    setConfig((prev) => ({ ...prev, ...data }));
                }
            } catch (error) {
                console.error('Failed to load integration settings:', error);
            } finally {
                setInitialLoading(false);
            }
        };
        loadConfig();
    }, []);

    // Load Facebook connection status on mount
    useEffect(() => {
        const loadFbStatus = async () => {
            setFbLoading(true);
            try {
                const data = await facebookService.getStatus();
                setFbConnected(data.connected);
                setFbPages(data.pages || []);
            } catch (error) {
                console.error('Failed to load Facebook status:', error);
            } finally {
                setFbLoading(false);
            }
        };
        const loadSources = async () => {
            try {
                const sources = await sourcesService.getAll();
                const names = (sources || []).map(d => d.name).filter(Boolean);
                const defaults = ['Meta Lead Gen', 'WhatsApp Inbound', 'Manual Entry', 'Walk-in', 'Instagram'];
                const merged = Array.from(new Set([...names, ...defaults]));
                setLeadSources(merged);
            } catch (e) {
                console.error('Failed to load lead sources:', e);
                setLeadSources(['Meta Lead Gen', 'WhatsApp Inbound', 'Manual Entry', 'Walk-in', 'Instagram']);
            }
        };
        loadFbStatus();
        loadSources();
    }, []);

    // Handle Facebook OAuth callback redirect params
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const fbStatus = params.get('fb_status');
        const fbError = params.get('fb_error');

        if (fbStatus === 'success') {
            const pageCount = params.get('fb_pages') || '1';
            setSaveMessage(`Facebook connected successfully! ${pageCount} page(s) linked.`);
            setSaveSuccess(true);
            setActiveTab('meta');
            // Reload FB status
            facebookService.getStatus().then(data => {
                setFbConnected(data.connected);
                setFbPages(data.pages || []);
                window.dispatchEvent(new Event('fb_status_changed'));
            }).catch(console.error);
        } else if (fbStatus === 'denied') {
            setSaveError(`Facebook connection was denied: ${fbError || 'User cancelled'}`);
            setActiveTab('meta');
        } else if (fbStatus === 'error') {
            setSaveError(`Facebook connection failed: ${fbError || 'Unknown error'}`);
            setActiveTab('meta');
        }

        // Clean up URL params
        if (fbStatus) {
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, '', cleanUrl);
        }
    }, []);

    // Ensure integration source exists in lead_sources collection
    const ensureIntegrationSource = async (sourceName) => {
        const sourcesRef = collection(db, 'artifacts', appId, 'public', 'data', 'lead_sources');
        const snapshot = await getDocs(sourcesRef);
        const existingSources = snapshot.docs.map(d => d.data().name);

        if (!existingSources.includes(sourceName)) {
            await addDoc(sourcesRef, {
                name: sourceName,
                isIntegration: true,
                createdAt: serverTimestamp()
            });
            return true;
        }
        return false;
    };

    const handleSave = async () => {
        setSaving(true);

        const addedSources = [];

        if (config.fbPageId && config.fbAccessToken) {
            const added = await ensureIntegrationSource(INTEGRATION_SOURCES.meta);
            if (added) addedSources.push(INTEGRATION_SOURCES.meta);
        }

        const hasWaCloud = config.waProvider === 'whatsapp_cloud' && config.waPhoneNumberId && config.waCloudAccessToken;
        const hasWaOther = config.waProvider !== 'whatsapp_cloud' && config.waApiKey && config.waBusinessNumber;

        if (hasWaCloud || hasWaOther) {
            const added = await ensureIntegrationSource(INTEGRATION_SOURCES.whatsapp);
            if (added) addedSources.push(INTEGRATION_SOURCES.whatsapp);
        }

        try {
            await settingsService.update('integrations', config);

            setSaving(false);

            if (addedSources.length > 0) {
                setSaveMessage(`Integration Settings Saved!\n\nAutomatically added lead sources:\n• ${addedSources.join('\n• ')}`);
            } else {
                setSaveMessage('Integration Settings Saved Successfully!');
            }
            setSaveSuccess(true);
        } catch (error) {
            console.error('Failed to save settings:', error);
            setSaveError('Failed to save integration settings.');
            setSaving(false);
        }
    };

    // Check Google Calendar connection status on mount and load calendars
    useEffect(() => {
        const checkGoogleConnection = async () => {
            if (config.googleCalendarClientId && config.googleCalendarApiKey) {
                try {
                    await loadGoogleScripts();
                    const connected = isSignedIn();
                    setGoogleCalendarConnected(connected);

                    if (connected) {
                        await loadAvailableCalendars();
                        if (config.googleCalendarSelectedId) {
                            setSelectedCalendar(config.googleCalendarSelectedId);
                        }
                    }
                } catch (e) {
                    console.log('Google Calendar not connected');
                }
            }
        };
        checkGoogleConnection();
    }, [config.googleCalendarClientId, config.googleCalendarApiKey]);

    const loadAvailableCalendars = async () => {
        setLoadingCalendars(true);
        try {
            const calendars = await listCalendars();
            setAvailableCalendars(calendars);
        } catch (error) {
            console.error('Failed to load calendars:', error);
            setAvailableCalendars([]);
        }
        setLoadingCalendars(false);
    };

    const handleGoogleConnect = async () => {
        if (!config.googleCalendarClientId || !config.googleCalendarApiKey) {
            setSaveError('Please enter your Google Calendar Client ID and API Key first, then save.');
            return;
        }

        setConnectingGoogle(true);
        try {
            await initGoogleCalendarApi(config.googleCalendarClientId, config.googleCalendarApiKey);
            await requestAuthorization();
            setGoogleCalendarConnected(true);
            await loadAvailableCalendars();
            setSaveMessage('Successfully connected to Google Calendar!');
            setSaveSuccess(true);
        } catch (error) {
            console.error('Google Calendar connection failed:', error);
            setSaveError('Failed to connect to Google Calendar. Please check your credentials.');
        }
        setConnectingGoogle(false);
    };

    const handleGoogleDisconnect = () => {
        googleSignOut();
        setAvailableCalendars([]);
        setGoogleCalendarConnected(false);
        setSaveMessage('Disconnected from Google Calendar.');
        setSaveSuccess(true);
    };

    const tabs = [
        { id: 'meta', label: t('metaLeads'), icon: Facebook },
        { id: 'whatsapp', label: t('whatsapp'), icon: MessageCircle },
        { id: 'ai', label: t('aiGemini'), icon: Sparkles },
        { id: 'invoicing', label: t('invoicing'), icon: CreditCard },
        { id: 'notifications', label: t('alerts'), icon: Bell },
        { id: 'calendar', label: t('calendar'), icon: Calendar }
    ];

    return (
        <div className="space-y-4 animate-fade-in">
            {/* Header */}
            <div className="mb-4">
                <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
                    {t('integrationsHub')}
                </h2>
                <p className="text-gray-500 mt-1">{t('connectExternalPlatforms')}</p>
            </div>

            {/* Card wrapper */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 flex flex-col overflow-hidden relative">

                {/* Error Toast */}
                {saveError && (
                    <div className="absolute top-4 right-4 z-[9999] flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-sm font-medium bg-red-600 text-white animate-fade-in">
                        <span className="flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> {saveError}</span>
                        <button onClick={() => setSaveError('')} className="ml-2 opacity-70 hover:opacity-100">✕</button>
                    </div>
                )}

                {/* Success Modal Overlay */}
                {saveSuccess && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center p-4 bg-white/80 backdrop-blur-sm animate-in fade-in duration-200">
                        <div className="bg-white border rounded-2xl shadow-2xl p-8 max-w-sm w-full text-center transform animate-in zoom-in-95 duration-200">
                            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 mb-6">
                                <Check className="h-8 w-8 text-green-600" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{t('success')}!</h3>
                            <p className="text-gray-600 whitespace-pre-line leading-relaxed">
                                {saveMessage}
                            </p>
                            <button
                                onClick={() => setSaveSuccess(false)}
                                className="mt-8 w-full inline-flex justify-center rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 transition-colors"
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                )}

                {/* Tabs */}
                <div className="flex border-b bg-gray-50 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex-1 min-w-max px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 border-b-2 transition-colors ${activeTab === tab.id
                                ? 'border-blue-600 text-blue-600 bg-white'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="p-6 flex-1 overflow-y-auto bg-slate-50">
                    {initialLoading ? (
                        <div className="space-y-6">
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                                <div className="p-5 border-b bg-gray-50 flex items-center gap-3">
                                    <Skeleton variant="circular" width={32} height={32} animation="wave" />
                                    <div>
                                        <Skeleton width={200} height={20} animation="wave" className="mb-1" />
                                        <Skeleton width={150} height={16} animation="wave" />
                                    </div>
                                </div>
                                <div className="p-5 space-y-4">
                                    {[1, 2].map(i => (
                                        <div key={i}>
                                            <Skeleton width={120} height={16} animation="wave" className="mb-2 uppercase" />
                                            <Skeleton width="100%" height={45} animation="wave" className="rounded-lg" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                    {/* Meta/Facebook Lead Gen — OAuth Flow */}
                    {activeTab === 'meta' && (
                        <div className="space-y-6">
                            <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4 border-b border-gray-100 pb-4">
                                    <div className="p-2 bg-blue-100 text-blue-600 rounded-lg w-fit">
                                        <Facebook className="w-6 h-6" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-bold text-gray-900">{t('metaIntegration')}</h3>
                                        <p className="text-xs text-gray-500">{t('metaIntegrationDesc')}</p>
                                    </div>
                                    {/* Connection status badge */}
                                    {!fbLoading && (
                                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${
                                            fbConnected
                                                ? 'bg-green-100 text-green-700'
                                                : 'bg-gray-100 text-gray-500'
                                        }`}>
                                            {fbConnected ? (
                                                <><CheckCircle2 className="w-3.5 h-3.5" /> {t('connected') || 'Connected'}</>
                                            ) : (
                                                <><AlertCircle className="w-3.5 h-3.5" /> {t('notConnected') || 'Not Connected'}</>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {fbLoading ? (
                                    <div className="flex items-center justify-center py-8">
                                        <Loader className="w-6 h-6 animate-spin text-blue-500" />
                                        <span className="ml-2 text-sm text-gray-500">{t('checkingFbConnection') || 'Checking Facebook connection...'}</span>
                                    </div>
                                ) : fbConnected ? (
                                    /* ===== CONNECTED STATE ===== */
                                    <div className="space-y-4">
                                        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                                            <div className="flex items-center gap-2 mb-3">
                                                <CheckCircle2 className="w-5 h-5 text-green-600" />
                                                <p className="font-semibold text-green-800">{t('fbPagesConnected') || 'Facebook Pages Connected'}</p>
                                            </div>
                                            <p className="text-xs text-green-700 mb-3">
                                                {t('fbPagesConnectedDesc') || 'Your Facebook pages are connected via OAuth. Lead ads from these pages will automatically appear in your CRM.'}
                                            </p>
                                        </div>

                                        {/* Connected Pages List */}
                                        <div className="space-y-3">
                                            {fbPages.map((page) => (
                                                <div key={page.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all gap-4">
                                                    <div className="flex items-center gap-4">
                                                        {/* Facebook Logo */}
                                                        <div className="w-12 h-12 flex-shrink-0 rounded-xl overflow-hidden shadow-sm" style={{ background: 'linear-gradient(135deg, #1877F2 0%, #0C5DC7 100%)' }}>
                                                            <div className="w-full h-full flex items-center justify-center">
                                                                <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                                                </svg>
                                                            </div>
                                                        </div>
                                                        <div className="min-w-0">
                                                            <p className="font-bold text-gray-900 text-sm truncate">{page.pageName}</p>
                                                            <p className="text-[11px] text-gray-500 mt-0.5">
                                                                {t('idLabel') || 'ID'}: <span className="font-mono">{page.pageId}</span>
                                                            </p>
                                                            <p className="text-[11px] text-gray-400">
                                                                {t('connectedBy') || 'Connected by'} <span className="font-medium text-gray-600">{page.connectedUser?.username || t('admin') || 'Admin'}</span>
                                                                {' · '}{formatDate(page.createdAt, { month: 'short', day: 'numeric', year: 'numeric' })}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={async () => {
                                                            setFbDisconnecting(page.pageId);
                                                            try {
                                                                await facebookService.disconnect(page.pageId);
                                                                setFbPages(prev => prev.filter(p => p.pageId !== page.pageId));
                                                                if (fbPages.length <= 1) setFbConnected(false);
                                                                setSaveMessage(`Disconnected "${page.pageName}" successfully.`);
                                                                setSaveSuccess(true);
                                                                window.dispatchEvent(new Event('fb_status_changed'));
                                                            } catch (err) {
                                                                setSaveError(`Failed to disconnect: ${err.message}`);
                                                            } finally {
                                                                setFbDisconnecting(null);
                                                            }
                                                        }}
                                                        disabled={fbDisconnecting === page.pageId}
                                                        className="w-full sm:w-auto flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-semibold text-red-600 bg-red-50 border border-red-100 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                                                    >
                                                        {fbDisconnecting === page.pageId ? (
                                                            <><Loader className="w-3.5 h-3.5 animate-spin" /> {t('disconnecting') || 'Disconnecting...'}</>
                                                        ) : (
                                                            <><Unlink className="w-3.5 h-3.5" /> {t('disconnect') || 'Disconnect'}</>
                                                        )}
                                                    </button>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Add another page button */}
                                        <button
                                            onClick={async () => {
                                                setFbConnecting(true);
                                                try {
                                                    const { authUrl } = await facebookService.getAuthUrl();
                                                    window.location.href = authUrl;
                                                } catch (err) {
                                                    setSaveError(`Failed to start Facebook connection: ${err.message}`);
                                                    setFbConnecting(false);
                                                }
                                            }}
                                            disabled={fbConnecting}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 transition-colors disabled:opacity-50"
                                        >
                                            {fbConnecting ? (
                                                <><Loader className="w-4 h-4 animate-spin" /> {t('redirectingToFacebook') || 'Redirecting to Facebook...'}</>
                                            ) : (
                                                <><Link2 className="w-4 h-4" /> {t('connectAnotherPage') || 'Connect Another Page'}</>
                                            )}
                                        </button>
                                    </div>
                                ) : (
                                    /* ===== NOT CONNECTED STATE ===== */
                                    <div className="space-y-4">
                                        <div className="text-center py-6">
                                            <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                                                <Facebook className="w-8 h-8 text-blue-600" />
                                            </div>
                                            <h4 className="font-bold text-gray-900 mb-2">{t('connectYourFacebookPage') || 'Connect Your Facebook Page'}</h4>
                                            <p className="text-sm text-gray-500 max-w-md mx-auto">
                                                {t('connectFacebookDesc') || 'Click the button below to securely connect your Facebook Page via OAuth. This grants Area 51 CRM permission to receive lead ads automatically.'}
                                            </p>
                                        </div>

                                        <button
                                            id="fb-connect-button"
                                            onClick={async () => {
                                                setFbConnecting(true);
                                                try {
                                                    const { authUrl } = await facebookService.getAuthUrl();
                                                    window.location.href = authUrl;
                                                } catch (err) {
                                                    setSaveError(`Failed to start Facebook connection: ${err.message}`);
                                                    setFbConnecting(false);
                                                }
                                            }}
                                            disabled={fbConnecting}
                                            className="w-full sm:w-auto sm:min-w-[280px] mx-auto flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-lg transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {fbConnecting ? (
                                                <><Loader className="w-5 h-5 animate-spin" /> {t('redirectingToFacebook') || 'Redirecting to Facebook...'}</>
                                            ) : (
                                                <><Facebook className="w-5 h-5" /> {t('connectWithFacebook') || 'Connect with Facebook'}</>
                                            )}
                                        </button>

                                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                                            <h4 className="font-bold text-blue-800 mb-2 text-sm">{t('howItWorks') || 'How It Works'}</h4>
                                            <ul className="text-xs text-blue-700 space-y-1.5">
                                                <li className="flex items-start gap-2"><span className="mt-0.5">1.</span> {t('fbStep1') || 'Click "Connect with Facebook" to open Meta\'s secure login'}</li>
                                                <li className="flex items-start gap-2">
                                                    <span className="mt-0.5">2.</span> 
                                                    <span>{t('fbStep2') || 'Grant permissions for business_management, pages_manage_ads, ads_management, pages_show_list, pages_read_engagement, pages_manage_metadata, and leads_retrieval'}</span>
                                                </li>
                                                <li className="flex items-start gap-2"><span className="mt-0.5">3.</span> {t('fbStep3') || 'Your page tokens are securely stored — no manual copy-paste needed'}</li>
                                                <li className="flex items-start gap-2"><span className="mt-0.5">4.</span> {t('fbStep4') || 'Lead ads will automatically flow into your CRM via webhooks'}</li>
                                            </ul>
                                        </div>

                                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                                            <p className="text-xs text-amber-800">
                                                <strong>{t('adminOnlyLabel') || 'Admin Only:'}</strong> {t('fbAdminOnlyNote') || 'Only admins need to connect Facebook. Your sales team will see leads automatically in the CRM dashboard — they don\'t need a Facebook login.'}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>

                        {/* Meta Ads reporting — feeds the dashboard's spend ledger
                            and per-ad-set Meta numbers (marketing sync reads
                            integrations.metaAdsToken / metaAdAccountId). Separate
                            from the OAuth page connection above: this is a
                            System User token with ads_read on the ad account,
                            pasted once. It is stored server-side and only ever
                            used by the server. */}
                        <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4 border-b border-gray-100 pb-4">
                                <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg w-fit">
                                    <Facebook className="w-6 h-6" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-bold text-gray-900">{t('metaAdsReporting') || 'Meta Ads Reporting'}</h3>
                                    <p className="text-xs text-gray-500">{t('metaAdsReportingDesc') || 'Powers the dashboard’s ad-spend ledger and per-ad-set Meta numbers. Syncs nightly. Uses the connected Facebook page’s token unless a token is pasted here.'}</p>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                        {t('metaAdAccountIdLabel') || 'Ad Account ID'}
                                    </label>
                                    <input
                                        type="text"
                                        value={config.metaAdAccountId || ''}
                                        onChange={(e) => setConfig({ ...config, metaAdAccountId: e.target.value.trim() })}
                                        placeholder="act_XXXXXXXXXX"
                                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                        {t('metaAdsTokenLabel') || 'Meta Token (optional)'}
                                    </label>
                                    <input
                                        type="password"
                                        autoComplete="new-password"
                                        value={config.metaAdsToken || ''}
                                        onChange={(e) => setConfig({ ...config, metaAdsToken: e.target.value.trim() })}
                                        placeholder="EAA..."
                                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                    />
                                    <p className="text-[11px] text-gray-400 mt-1">
                                        {t('metaAdsTokenNote') || 'Paste a Graph API Explorer token with ads_read once; the CRM extends and renews it automatically. Leave empty to use the connected Facebook page’s token instead (the page must have been connected with the ads permission). The Ad Account ID is found automatically when the token sees exactly one account. Stored server-side.'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Instagram DM names — the webhook resolves sender
                            names via graph.instagram.com using this account
                            token (Instagram API panel → Generate token).
                            Cosmetic only: capture works without it, leads just
                            arrive as "Instagram User". */}
                        <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4 border-b border-gray-100 pb-4">
                                <div className="p-2 bg-pink-100 text-pink-600 rounded-lg w-fit">
                                    <MessageCircle className="w-6 h-6" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-bold text-gray-900">{t('igMessagingTitle') || 'Instagram Messaging'}</h3>
                                    <p className="text-xs text-gray-500">{t('igMessagingDesc') || 'Resolves Instagram DM sender names on captured leads. Without it, leads still arrive — just named "Instagram User".'}</p>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                    {t('igAccessTokenLabel') || 'Instagram Access Token'}
                                </label>
                                <input
                                    type="password"
                                    autoComplete="new-password"
                                    value={config.igAccessToken || ''}
                                    onChange={(e) => setConfig({ ...config, igAccessToken: e.target.value.trim() })}
                                    placeholder="IGAA..."
                                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                                <p className="text-[11px] text-gray-400 mt-1">
                                    {t('igAccessTokenNote') || 'App dashboard → Instagram API setup → Generate token for @area51marquee. Self-renewing: the server refreshes it automatically as DMs arrive — re-paste only if Instagram goes silent for 60+ days.'}
                                </p>
                            </div>
                        </div>
                        </div>
                    )}

                    {/* WhatsApp Business API */}
                    {activeTab === 'whatsapp' && (
                        <div className="space-y-6">
                            <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4 border-b border-gray-100 pb-4">
                                    <div className="p-2 bg-green-100 text-green-600 rounded-lg w-fit">
                                        <MessageCircle className="w-6 h-6" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-bold text-gray-900">{t('whatsappBusinessApi') || 'WhatsApp Business API'}</h3>
                                        <p className="text-xs text-gray-500">{t('whatsappApiDesc')}</p>
                                    </div>
                                    <a
                                        href="https://business.facebook.com/wa/manage/phone-numbers/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors shrink-0"
                                    >
                                        <ExternalLink className="w-3.5 h-3.5" />
                                        {t('whatsappQualityRating') || 'WhatsApp Quality Rating'}
                                    </a>
                                </div>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                            {t('providerLabel') || 'Provider'}
                                        </label>
                                        <Dropdown>
                                            <DropdownTrigger>
                                                <button className="w-full p-3 border rounded-lg text-sm outline-none focus:border-green-400 flex justify-between items-center text-left bg-white">
                                                    {config.waProvider === 'whatsapp_cloud' ? (
                                                        <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-500" /> WhatsApp Cloud API (Meta Official)</div>
                                                    ) : 
                                                     config.waProvider === 'twilio' ? 'Twilio' : 
                                                     config.waProvider === 'wati' ? 'Wati' : 'Aisensy'}
                                                    <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                                                </button>
                                            </DropdownTrigger>
                                            <DropdownPopover className="w-full">
                                                <DropdownMenu
                                                    selectedKeys={new Set([config.waProvider])}
                                                    selectionMode="single"
                                                    onSelectionChange={(keys) => setConfig({ ...config, waProvider: Array.from(keys)[0] })}
                                                >
                                                    <DropdownItem id="whatsapp_cloud"><div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-500" /> WhatsApp Cloud API (Meta Official)</div></DropdownItem>
                                                    <DropdownItem id="twilio">Twilio</DropdownItem>
                                                    <DropdownItem id="wati">Wati</DropdownItem>
                                                    <DropdownItem id="aisensy">Aisensy</DropdownItem>
                                                </DropdownMenu>
                                            </DropdownPopover>
                                        </Dropdown>
                                        {config.waProvider === 'whatsapp_cloud' && (
                                            <p className="text-xs text-green-600 mt-1 font-medium flex items-start gap-1">
                                                <Globe className="w-4 h-4 shrink-0 mt-0.5" /> {t('whatsappCloudNote') || 'Official WhatsApp Business Cloud API — free tier available, no third-party needed.'}
                                            </p>
                                        )}
                                    </div>

                                    {/* WhatsApp Cloud API Fields */}
                                    {config.waProvider === 'whatsapp_cloud' && (
                                        <>
                                            <div>
                                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                                    {t('phoneNumberIdLabel') || 'Phone Number ID'}
                                                </label>
                                                <input
                                                    className="w-full p-3 border rounded-lg text-sm outline-none font-mono focus:border-green-400"
                                                    value={config.waPhoneNumberId}
                                                    onChange={(e) => setConfig({ ...config, waPhoneNumberId: e.target.value })}
                                                    placeholder="123456789012345"
                                                />
                                                <p className="text-xs text-gray-400 mt-1">
                                                    Meta Developer Console → App → WhatsApp → API Setup → <strong>{t('phoneNumberIdLabel') || 'Phone Number ID'}</strong>
                                                </p>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                                    {t('permanentAccessTokenLabel') || 'Permanent Access Token'}
                                                </label>
                                                <input
                                                    type="password"
                                                    className="w-full p-3 border rounded-lg text-sm outline-none font-mono focus:border-green-400"
                                                    value={config.waCloudAccessToken}
                                                    onChange={(e) => setConfig({ ...config, waCloudAccessToken: e.target.value })}
                                                    placeholder="EAAxxxxxxx..."
                                                />
                                                <p className="text-xs text-gray-400 mt-1">
                                                    {t('permanentTokenHint') || 'Use System User permanent token (do not use temporary token)'}
                                                </p>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                                    {t('wabaIdLabel') || 'WhatsApp Business Account ID (WABA ID)'}
                                                </label>
                                                <input
                                                    className="w-full p-3 border rounded-lg text-sm outline-none font-mono focus:border-green-400"
                                                    value={config.waBusinessAccountId}
                                                    onChange={(e) => setConfig({ ...config, waBusinessAccountId: e.target.value })}
                                                    placeholder="987654321098765"
                                                />
                                                <p className="text-xs text-gray-400 mt-1">
                                                    Meta Developer Console → WhatsApp → <strong>{t('wabaIdLabel') || 'WhatsApp Business Account ID'}</strong>
                                                </p>
                                            </div>
                                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                                <p className="text-xs text-green-800 font-semibold mb-1 flex items-center gap-1"><ClipboardList className="w-4 h-4" /> {t('webhookSetupLabel') || 'Webhook Setup (for Inbound Messages)'}</p>
                                                <p className="text-xs text-green-700 mb-1">Meta App → WhatsApp → Configuration → Webhook URL:</p>
                                                <code className="text-xs bg-green-100 px-2 py-1 rounded font-mono text-green-900 block break-all">
                                                    https://your-server.com/webhook/whatsapp
                                                </code>
                                                <p className="text-xs text-green-700 mt-1">Verify Token: <code className="bg-green-100 px-1 rounded">area51_crm</code></p>
                                            </div>
                                        </>
                                    )}
                                    {config.waProvider !== 'whatsapp_cloud' && (
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                                {t('apiKeySid')}
                                            </label>
                                            <input
                                                type="password"
                                                className="w-full p-3 border rounded-lg text-sm outline-none font-mono focus:border-green-400"
                                                value={config.waApiKey}
                                                onChange={(e) => setConfig({ ...config, waApiKey: e.target.value })}
                                            />
                                        </div>
                                    )}
                                    {config.waProvider === 'twilio' && (
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                                {t('authToken')}
                                            </label>
                                            <input
                                                type="password"
                                                className="w-full p-3 border rounded-lg text-sm outline-none font-mono focus:border-green-400"
                                                value={config.waApiSecret}
                                                onChange={(e) => setConfig({ ...config, waApiSecret: e.target.value })}
                                            />
                                        </div>
                                    )}
                                    {config.waProvider === 'wati' && (
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                                {t('apiEndpoint')}
                                            </label>
                                            <input
                                                className="w-full p-3 border rounded-lg text-sm outline-none font-mono focus:border-green-400"
                                                value={config.waApiEndpoint}
                                                onChange={(e) => setConfig({ ...config, waApiEndpoint: e.target.value })}
                                                placeholder="https://live-server-xxxx.wati.io"
                                            />
                                        </div>
                                    )}
                                    {config.waProvider !== 'whatsapp_cloud' && (
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                                {t('businessNumber')}
                                            </label>
                                            <input
                                                className="w-full p-3 border rounded-lg text-sm outline-none focus:border-green-400"
                                                value={config.waBusinessNumber}
                                                onChange={(e) => setConfig({ ...config, waBusinessNumber: e.target.value })}
                                                placeholder="923001234567"
                                            />
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                            {t('defaultGreeting')}
                                        </label>
                                        <textarea
                                            className="w-full p-3 border rounded-lg text-sm outline-none h-24 resize-none focus:border-green-400"
                                            value={config.waDefaultMessage}
                                            onChange={(e) => setConfig({ ...config, waDefaultMessage: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Auto Greeting Section */}
                            <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 border-b border-gray-100 pb-4">
                                    <div className="flex items-center gap-3 flex-1">
                                        <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                                            <Sparkles className="w-6 h-6" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-bold text-gray-900">{t('autoGreetingTitle')}</h3>
                                            <p className="text-xs text-gray-500">{t('autoGreetingSubtitle') || 'Automatically send WhatsApp greeting to new leads'}</p>
                                        </div>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={config.autoGreetingEnabled}
                                            onChange={(e) =>
                                                setConfig({ ...config, autoGreetingEnabled: e.target.checked })
                                            }
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                    </label>
                                </div>

                                {config.autoGreetingEnabled && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
                                                {t('enabledLeadSources') || 'Enabled Lead Sources'}
                                            </label>
                                            <div className="flex flex-wrap gap-2">
                                                {leadSources.map(source => (
                                                    <label key={source} className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg border cursor-pointer hover:bg-gray-100 transition-colors">
                                                        <input 
                                                            type="checkbox"
                                                            checked={config.autoGreetingSources?.includes(source) || false}
                                                            onChange={(e) => {
                                                                const current = config.autoGreetingSources || [];
                                                                if (e.target.checked) {
                                                                    setConfig({ ...config, autoGreetingSources: [...current, source] });
                                                                } else {
                                                                    setConfig({ ...config, autoGreetingSources: current.filter(s => s !== source) });
                                                                }
                                                            }}
                                                            className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                                                        />
                                                        <span className="text-sm font-medium text-gray-700">{t(source) || source}</span>
                                                    </label>
                                                ))}
                                                {leadSources.length === 0 && (
                                                    <span className="text-xs text-gray-400">Loading sources...</span>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-400 mt-1">{t('selectLeadSourcesHint') || 'Select the lead sources that should trigger this auto greeting.'}</p>
                                        </div>

                                        {config.waProvider === 'wati' ? (
                                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <AlertCircle className="w-4 h-4 text-amber-600" />
                                                    <span className="text-xs font-bold text-amber-800 uppercase">{t('watiApprovedTemplateControlled') || 'WATI Approved Template Controlled'}</span>
                                                </div>
                                                <p className="text-xs text-amber-700">
                                                    {t('watiApprovedTemplateDesc') || 'When WATI is selected as your WhatsApp provider, auto-greetings use the WATI-approved template initial_outreach. Custom message text box is disabled here because WATI requires pre-approved templates for new outbound conversations.'}
                                                </p>
                                            </div>
                                        ) : (
                                            <>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
                                                        {t('quickTemplates')}
                                                    </label>
                                                    <div className="flex gap-2 flex-wrap">
                                                        <button
                                                            type="button"
                                                            onClick={() => setConfig({ ...config, autoGreetingMessage: `Hello {{name}}! 👋\n\nThank you for your interest in Area 51 Banquet Hall. We're excited to help you plan your special event!\n\nOne of our team members will contact you shortly.\n\n📋 Conversations are recorded for quality purposes.\n\nBest regards,\nArea 51 Team` })}
                                                            className="px-3 py-1.5 text-xs bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-1"
                                                        >
                                                            <Globe className="w-3 h-3" /> English
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setConfig({ ...config, autoGreetingMessage: `Assalam o Alaikum {{name}}! 👋\n\nArea 51 Banquet Hall mein inquiry ka shukriya! Hamari team jaldi aap se rabta karegi.\n\nAgar koi sawal ho toh is message ka jawab dein.\n\n📋 Is number pe baat cheet service quality ke liye record ki jati hai.\n\nShukriya,\nArea 51 Team` })}
                                                            className="px-3 py-1.5 text-xs bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors flex items-center gap-1"
                                                        >
                                                            <Flag className="w-3 h-3" /> Urdu
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setConfig({ ...config, autoGreetingMessage: `Assalam o Alaikum {{name}}! 👋\n\nThank you for contacting Area 51 Banquet Hall! Our team will reach out to you shortly.\n\nFeel free to reply if you have any questions.\n\n📋 Conversations recorded for quality.\n\nRegards,\nArea 51 Team` })}
                                                            className="px-3 py-1.5 text-xs bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors flex items-center gap-1"
                                                        >
                                                            <ListChecks className="w-3 h-3" /> Mixed
                                                        </button>
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                                        {t('autoGreetingMessageLabel')}
                                                    </label>
                                                    <textarea
                                                        className="w-full p-3 border rounded-lg text-sm outline-none h-32 resize-none focus:border-blue-400"
                                                        value={config.autoGreetingMessage}
                                                        onChange={(e) => setConfig({ ...config, autoGreetingMessage: e.target.value })}
                                                        placeholder={t('autoGreetingPlaceholder')}
                                                    />
                                                    <p className="text-xs text-gray-400 mt-1">
                                                        {t('autoGreetingNote')}
                                                    </p>
                                                </div>
                                            </>
                                        )}
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                            <p className="text-xs text-blue-700">
                                                <strong>{t('howItWorks')}:</strong> {t('autoGreetingHowItWorksDesc') || 'When a new lead is created (from Meta, WhatsApp, or manual entry), this greeting will be automatically sent via WhatsApp within 1 minute.'}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Gemini AI */}
                    {activeTab === 'ai' && (
                        <div className="space-y-6">
                            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                                <div className="flex items-center gap-3 mb-4 border-b border-gray-100 pb-4">
                                    <div className="p-2 bg-purple-100 text-purple-600 rounded-lg">
                                        <Sparkles className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-gray-900">{t('googleGeminiAi')}</h3>
                                        <p className="text-xs text-gray-500">{t('geminiAiDesc')}</p>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                            {t('geminiApiKey')}
                                        </label>
                                        <input
                                            type="password"
                                            className="w-full p-3 border rounded-lg text-sm outline-none font-mono focus:border-purple-400"
                                            value={config.geminiApiKey}
                                            onChange={(e) => setConfig({ ...config, geminiApiKey: e.target.value })}
                                            placeholder="AIzaSy..."
                                        />
                                        <p className="text-xs text-gray-400 mt-1">
                                            {t('getFromGoogleAi')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                                <h4 className="font-bold text-purple-800 mb-2">{t('aiFeaturesEnabled')}</h4>
                                <ul className="text-sm text-purple-700 space-y-1">
                                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> {t('aiFeature1')}</li>
                                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> {t('aiFeature2')}</li>
                                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> {t('aiFeature3')}</li>
                                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> {t('aiFeature4')}</li>
                                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> {t('aiFeature5')}</li>
                                </ul>
                                <p className="text-xs text-purple-600 mt-3">
                                    <strong>Note:</strong> Auto greeting is configured in the WhatsApp tab
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Invoicing */}
                    {activeTab === 'invoicing' && (
                        <div className="space-y-6">
                            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                                <div className="flex items-center gap-3 mb-4 border-b border-gray-100 pb-4">
                                    <div className="p-2 bg-amber-100 text-amber-600 rounded-lg">
                                        <CreditCard className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-gray-900">{t('invoicingSoftware')}</h3>
                                        <p className="text-xs text-gray-500">{t('invoicingDesc')}</p>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                            {t('invoicingApiEndpoint')}
                                        </label>
                                        <input
                                            className="w-full p-3 border rounded-lg text-sm outline-none font-mono focus:border-amber-400"
                                            value={config.invoicingApiEndpoint}
                                            onChange={(e) =>
                                                setConfig({ ...config, invoicingApiEndpoint: e.target.value })
                                            }
                                            placeholder="https://your-invoicing-software.com"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                            {t('apiKeyLabel') || 'API Key'}
                                        </label>
                                        <input
                                            type="password"
                                            className="w-full p-3 border rounded-lg text-sm outline-none font-mono focus:border-amber-400"
                                            value={config.invoicingApiKey}
                                            onChange={(e) => setConfig({ ...config, invoicingApiKey: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                                <h4 className="font-bold text-amber-800 mb-2">{t('integrationBehavior')}</h4>
                                <ul className="text-sm text-amber-700 space-y-1">
                                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> {t('behavior1')}</li>
                                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> {t('behavior2')}</li>
                                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> {t('behavior3')}</li>
                                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> {t('behavior4')}</li>
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* Notifications */}
                    {activeTab === 'notifications' && (
                        <div className="space-y-6">
                            {/* Browser Notifications */}
                            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                                <div className="flex items-center gap-3 mb-4 border-b border-gray-100 pb-4">
                                    <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
                                        <Bell className="w-6 h-6" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-bold text-gray-900">{t('browserNotifications') || 'Browser Notifications'}</h3>
                                        <p className="text-xs text-gray-500">{t('browserNotificationsDesc')}</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={config.browserNotificationsEnabled}
                                            onChange={(e) =>
                                                setConfig({ ...config, browserNotificationsEnabled: e.target.checked })
                                            }
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                                    </label>
                                </div>
                                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                                    <p className="text-xs text-indigo-700">
                                        {t('browserNotificationsNote') || 'Note: You\'ll need to allow notifications when prompted by your browser. Works best on desktop browsers.'}
                                    </p>
                                </div>
                            </div>

                            {/* SMS Notifications */}
                            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                                <div className="flex items-center gap-3 mb-4 border-b border-gray-100 pb-4">
                                    <div className="p-2 bg-emerald-100 text-emerald-600 rounded-lg">
                                        <Smartphone className="w-6 h-6" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-bold text-gray-900">{t('smsNotifications') || 'SMS Notifications (Twilio)'}</h3>
                                        <p className="text-xs text-gray-500">{t('smsNotificationsDesc')}</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={config.smsEnabled}
                                            onChange={(e) =>
                                                setConfig({ ...config, smsEnabled: e.target.checked })
                                            }
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                                    </label>
                                </div>

                                {config.smsEnabled && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                                {t('twilioAccountSid')}
                                            </label>
                                            <input
                                                type="password"
                                                className="w-full p-3 border rounded-lg text-sm outline-none font-mono focus:border-emerald-400"
                                                value={config.smsTwilioSid}
                                                onChange={(e) => setConfig({ ...config, smsTwilioSid: e.target.value })}
                                                placeholder="ACxxxxxxxxxxxxxxx"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                                {t('twilioAuthToken')}
                                            </label>
                                            <input
                                                type="password"
                                                className="w-full p-3 border rounded-lg text-sm outline-none font-mono focus:border-emerald-400"
                                                value={config.smsTwilioToken}
                                                onChange={(e) => setConfig({ ...config, smsTwilioToken: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                                {t('twilioPhoneNumber')}
                                            </label>
                                            <input
                                                className="w-full p-3 border rounded-lg text-sm outline-none focus:border-emerald-400"
                                                value={config.smsTwilioNumber}
                                                onChange={(e) => setConfig({ ...config, smsTwilioNumber: e.target.value })}
                                                placeholder="+1234567890"
                                            />
                                            <p className="text-xs text-gray-400 mt-1">
                                                Your Twilio phone number or Messaging Service SID
                                            </p>
                                        </div>

                                        <div className="border-t border-gray-100 pt-4 mt-4">
                                            <p className="text-xs font-bold text-gray-500 uppercase mb-3">
                                                {t('sendSmsFor')}
                                            </p>
                                            <div className="space-y-2">
                                                <label className="flex items-center gap-3 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        className="w-4 h-4 text-emerald-600 rounded"
                                                        checked={config.smsNotifyOnAssignment}
                                                        onChange={(e) =>
                                                            setConfig({ ...config, smsNotifyOnAssignment: e.target.checked })
                                                        }
                                                    />
                                                    <span className="text-sm text-gray-700">{t('newLeadAssignments')}</span>
                                                </label>
                                                <label className="flex items-center gap-3 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        className="w-4 h-4 text-emerald-600 rounded"
                                                        checked={config.smsNotifyOnEscalation}
                                                        onChange={(e) =>
                                                            setConfig({ ...config, smsNotifyOnEscalation: e.target.checked })
                                                        }
                                                    />
                                                    <span className="text-sm text-gray-700">{t('leadEscalations')}</span>
                                                </label>
                                                <label className="flex items-center gap-3 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        className="w-4 h-4 text-emerald-600 rounded"
                                                        checked={config.smsNotifyOnSiteVisit}
                                                        onChange={(e) =>
                                                            setConfig({ ...config, smsNotifyOnSiteVisit: e.target.checked })
                                                        }
                                                    />
                                                    <span className="text-sm text-gray-700">{t('siteVisitReminders')}</span>
                                                </label>
                                                <label className="flex items-center gap-3 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        className="w-4 h-4 text-emerald-600 rounded"
                                                        checked={config.smsNotifyOnQuoteFollowUp}
                                                        onChange={(e) =>
                                                            setConfig({ ...config, smsNotifyOnQuoteFollowUp: e.target.checked })
                                                        }
                                                    />
                                                    <span className="text-sm text-gray-700">{t('quoteFollowUp')}</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                                <h4 className="font-bold text-emerald-800 mb-2">{t('howSmsWorks')}</h4>
                                <ul className="text-sm text-emerald-700 space-y-1">
                                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> {t('smsBenefit1') || 'Employees receive SMS when leads are assigned'}</li>
                                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> {t('smsBenefit2') || 'Escalation alerts sent for stale leads (48h+)'}</li>
                                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> {t('smsBenefit3') || 'Site visit reminders sent day before'}</li>
                                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> {t('smsBenefit4') || 'Quote follow-up reminders after 3 days'}</li>
                                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> {t('smsBenefit5') || 'Uses employee phone from Admin Panel'}</li>
                                </ul>
                                <p className="text-xs text-emerald-600 mt-3">
                                    {t('smsCost') || 'Cost: ~PKR 3-5 per SMS via Twilio'}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Google Calendar */}
                    {activeTab === 'calendar' && (
                        <div className="space-y-6">
                            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                                <div className="flex items-center gap-3 mb-4 border-b border-gray-100 pb-4">
                                    <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                                        <Calendar className="w-6 h-6" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-bold text-gray-900">{t('googleCalendarIntegration')}</h3>
                                        <p className="text-xs text-gray-500">{t('googleCalendarDesc')}</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={config.googleCalendarEnabled}
                                            onChange={(e) =>
                                                setConfig({ ...config, googleCalendarEnabled: e.target.checked })
                                            }
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                    </label>
                                </div>

                                {config.googleCalendarEnabled && (
                                    <div className="space-y-4">
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                            <p className="text-xs text-blue-700">
                                                <strong>Quick Add:</strong> {t('quickAddDesc')}
                                            </p>
                                        </div>

                                        <div className="border-t border-gray-100 pt-4 mt-4">
                                            <div className="flex items-center gap-2 mb-3">
                                                <RefreshCw className="w-4 h-4 text-gray-500" />
                                                <p className="text-xs font-bold text-gray-500 uppercase">
                                                    {t('autoSync')}
                                                </p>
                                            </div>

                                            <div className="space-y-3">
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                                        {t('googleCloudClientId')}
                                                    </label>
                                                    <input
                                                        type="password"
                                                        className="w-full p-3 border rounded-lg text-sm outline-none font-mono focus:border-blue-400"
                                                        value={config.googleCalendarClientId}
                                                        onChange={(e) =>
                                                            setConfig({ ...config, googleCalendarClientId: e.target.value })
                                                        }
                                                        placeholder="xxxx.apps.googleusercontent.com"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                                        {t('googleCalendarApiKey')}
                                                    </label>
                                                    <input
                                                        type="password"
                                                        className="w-full p-3 border rounded-lg text-sm outline-none font-mono focus:border-blue-400"
                                                        value={config.googleCalendarApiKey}
                                                        onChange={(e) =>
                                                            setConfig({ ...config, googleCalendarApiKey: e.target.value })
                                                        }
                                                        placeholder="AIzaSy..."
                                                    />
                                                    <p className="text-xs text-gray-400 mt-1">
                                                        {t('getFrom') || 'Get from'}{' '}
                                                        <a
                                                            href="https://console.cloud.google.com/apis/credentials"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-blue-500 hover:underline inline-flex items-center gap-1"
                                                        >
                                                            {t('googleCloudConsole') || 'Google Cloud Console'} <ExternalLink className="w-3 h-3" />
                                                        </a>
                                                    </p>
                                                </div>

                                                {/* Connect/Disconnect Button */}
                                                <div className="flex items-center gap-3">
                                                    {googleCalendarConnected ? (
                                                        <>
                                                            <span className="flex items-center gap-2 text-sm text-green-600">
                                                                <Check className="w-4 h-4" /> Connected
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={handleGoogleDisconnect}
                                                                className="px-3 py-1.5 text-xs bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
                                                            >
                                                                {t('disconnect') || 'Disconnect'}
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={handleGoogleConnect}
                                                            disabled={connectingGoogle || !config.googleCalendarClientId || !config.googleCalendarApiKey}
                                                            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                                        >
                                                            {connectingGoogle ? (
                                                                <>
                                                                    <Loader className="w-4 h-4 animate-spin" /> {t('connectingGoogle') || 'Connecting...'}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Calendar className="w-4 h-4" /> {t('connectGoogleCalendar') || 'Connect Google Calendar'}
                                                                </>
                                                            )}
                                                        </button>
                                                    )}
                                                </div>

                                                {/* Calendar Selector */}
                                                {googleCalendarConnected && (
                                                    <div className="border-t border-gray-100 pt-4 mt-4">
                                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
                                                            {t('selectCalendar') || 'Select Calendar'}
                                                        </label>
                                                        {loadingCalendars ? (
                                                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                                                <Loader className="w-4 h-4 animate-spin" /> {t('loadingCalendars') || 'Loading calendars...'}
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <Dropdown>
                                                                    <DropdownTrigger>
                                                                        <button className="w-full p-3 border rounded-lg text-sm outline-none focus:border-blue-400 flex justify-between items-center text-left bg-white">
                                                                            {availableCalendars.length === 0 ? (t('primaryCalendar') || 'Primary Calendar') : 
                                                                             (availableCalendars.find(c => c.id === (config.googleCalendarSelectedId || 'primary'))?.name + (availableCalendars.find(c => c.id === (config.googleCalendarSelectedId || 'primary'))?.primary ? ' (Primary)' : '') || (t('primaryCalendar') || 'Primary Calendar'))}
                                                                            <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                                                                        </button>
                                                                    </DropdownTrigger>
                                                                    <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                                                                        <DropdownMenu
                                                                            selectedKeys={new Set([config.googleCalendarSelectedId || 'primary'])}
                                                                            selectionMode="single"
                                                                            onSelectionChange={(keys) => {
                                                                                const val = Array.from(keys)[0];
                                                                                setConfig({ ...config, googleCalendarSelectedId: val });
                                                                                setSelectedCalendar(val);
                                                                            }}
                                                                        >
                                                                            {availableCalendars.length === 0 ? (
                                                                                <DropdownItem id="primary">{t('primaryCalendar') || 'Primary Calendar'}</DropdownItem>
                                                                            ) : (
                                                                                availableCalendars.map((cal) => (
                                                                                    <DropdownItem key={cal.id} id={cal.id}>
                                                                                        {cal.name} {cal.primary ? '(Primary)' : ''}
                                                                                    </DropdownItem>
                                                                                ))
                                                                            )}
                                                                        </DropdownMenu>
                                                                    </DropdownPopover>
                                                                </Dropdown>
                                                                <p className="text-xs text-gray-400 mt-1">
                                                                    {t('allEventsSyncedToCalendar') || 'All CRM events will be synced to this calendar'}
                                                                </p>
                                                                {availableCalendars.length === 0 && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={loadAvailableCalendars}
                                                                        className="mt-2 text-xs text-blue-600 hover:underline flex items-center gap-1"
                                                                    >
                                                                        <RefreshCw className="w-3 h-3" /> {t('refreshCalendarList') || 'Refresh calendar list'}
                                                                    </button>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Auto Sync Toggle */}
                                                {googleCalendarConnected && (
                                                    <div className="border-t border-gray-100 pt-4 mt-4">
                                                        <div className="flex items-center justify-between mb-3">
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-900">{t('autoSyncEventsTitle') || 'Auto-Sync Events'}</p>
                                                                <p className="text-xs text-gray-500">{t('autoSyncEventsDesc') || 'Automatically create/update calendar events'}</p>
                                                            </div>
                                                            <label className="relative inline-flex items-center cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    className="sr-only peer"
                                                                    checked={config.googleCalendarAutoSync}
                                                                    onChange={(e) =>
                                                                        setConfig({ ...config, googleCalendarAutoSync: e.target.checked })
                                                                    }
                                                                />
                                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                                            </label>
                                                        </div>

                                                        {config.googleCalendarAutoSync && (
                                                            <div className="space-y-2 ml-1">
                                                                <p className="text-xs font-bold text-gray-500 uppercase mb-2">
                                                                    {t('syncTheseEventsLabel') || 'Sync These Events:'}
                                                                </p>
                                                                <label className="flex items-center gap-3 cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="w-4 h-4 text-blue-600 rounded"
                                                                        checked={config.googleCalendarSyncSiteVisits}
                                                                        onChange={(e) =>
                                                                            setConfig({ ...config, googleCalendarSyncSiteVisits: e.target.checked })
                                                                        }
                                                                    />
                                                                    <span className="text-sm text-gray-700">{t('siteVisitsLabel') || 'Site Visits'}</span>
                                                                </label>
                                                                <label className="flex items-center gap-3 cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="w-4 h-4 text-blue-600 rounded"
                                                                        checked={config.googleCalendarSyncCalls}
                                                                        onChange={(e) =>
                                                                            setConfig({ ...config, googleCalendarSyncCalls: e.target.checked })
                                                                        }
                                                                    />
                                                                    <span className="text-sm text-gray-700">{t('scheduledCallsLabel') || 'Scheduled Calls'}</span>
                                                                </label>
                                                                <label className="flex items-center gap-3 cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="w-4 h-4 text-blue-600 rounded"
                                                                        checked={config.googleCalendarSyncEvents}
                                                                        onChange={(e) =>
                                                                            setConfig({ ...config, googleCalendarSyncEvents: e.target.checked })
                                                                        }
                                                                    />
                                                                    <span className="text-sm text-gray-700">{t('eventDatesLabel') || 'Event Dates'}</span>
                                                                </label>
                                                                <label className="flex items-center gap-3 cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="w-4 h-4 text-blue-600 rounded"
                                                                        checked={config.googleCalendarSyncBookings}
                                                                        onChange={(e) =>
                                                                            setConfig({ ...config, googleCalendarSyncBookings: e.target.checked })
                                                                        }
                                                                    />
                                                                    <span className="text-sm text-gray-700">{t('finalBookingsLabel') || 'Final Bookings'}</span>
                                                                </label>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                                <h4 className="font-bold text-blue-800 mb-2">{t('howItWorks')}:</h4>
                                <ul className="text-sm text-blue-700 space-y-1">
                                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> <span><strong>{t('quickAdd')}</strong> - {t('calBenefit1') || 'Add any date to calendar with one click'}</span></li>
                                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> <span><strong>{t('siteVisitsLabel') || 'Site Visits'}</strong> - {t('calBenefit2') || 'Schedule venue visits with client details'}</span></li>
                                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> <span><strong>{t('scheduledCallsLabel') || 'Scheduled Calls'}</strong> - {t('calBenefit3') || 'Never miss a follow-up call'}</span></li>
                                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> <span><strong>{t('eventDatesLabel') || 'Event Dates'}</strong> - {t('calBenefit4') || 'Track client event dates'}</span></li>
                                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> <span><strong>{t('finalBookingsLabel') || 'Final Bookings'}</strong> - {t('calBenefit5') || 'Confirmed booking dates'}</span></li>
                                </ul>
                                <p className="text-xs text-blue-600 mt-3">
                                    <strong>{t('tipLabel') || 'Tip:'}</strong> {t('calTip') || 'Quick Add works without any API setup. Auto-sync requires Google Cloud credentials.'}
                                </p>
                            </div>
                        </div>
                    )}
                    </>
                    )}
                </div>

                {/* Save Button */}
                {activeTab !== 'facebook' && (
                    <div className="p-4 bg-white border-t border-gray-200 flex justify-end">
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl flex items-center gap-2 disabled:opacity-70"
                        >
                            {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}{' '}
                            {t('save')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
